import HttpService from './HttpService';

const TaskService = {
  // Returns a flat array of tasks. The backend now returns a paginated
  // envelope `{data: [...], pagination: {...}}`; we unwrap so existing callers
  // (Maintenance.jsx) keep getting a plain array. Pass `{all: true}` to
  // bypass server-side pagination, or `{page, limit}` for a single page.
  // Use `getTasksPaginated` if you need access to the pagination metadata.
  getTasks: async (params = {}) => {
    try {
      const response = await HttpService.get('/tasks', { params });
      const body = response.data;
      // Accept both the new envelope and the legacy raw-array shape so test
      // mocks built before pagination keep working.
      if (Array.isArray(body)) return body;
      if (body && Array.isArray(body.data)) return body.data;
      return [];
    } catch (error) {
      console.error('Error fetching tasks:', error);
      throw error;
    }
  },

  // Returns the full PaginatedTasks envelope including pagination metadata.
  getTasksPaginated: async (params = {}) => {
    try {
      const response = await HttpService.get('/tasks', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching paginated tasks:', error);
      throw error;
    }
  },

  getTask: async (id) => {
    try {
      const response = await HttpService.get(`/tasks/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching task ${id}:`, error);
      throw error;
    }
  },

  createTask: async (taskData) => {
    try {
      const response = await HttpService.post('/tasks', taskData);
      return response.data;
    } catch (error) {
      console.error('Error creating task:', error);
      throw error;
    }
  },

  updateTask: async (id, taskData) => {
    try {
      const response = await HttpService.put(`/tasks/${id}`, taskData);
      return response.data;
    } catch (error) {
      console.error(`Error updating task ${id}:`, error);
      throw error;
    }
  },

  completeTask: async (id, note) => {
    try {
      // The note is the optional Completion-record field — the body stays
      // empty for the common one-tap path.
      const response = await HttpService.post(`/tasks/${id}/complete`, note ? { note } : {});
      return response.data;
    } catch (error) {
      console.error(`Error completing task ${id}:`, error);
      throw error;
    }
  },

  getSuggestions: async () => {
    try {
      const response = await HttpService.get('/tasks/suggestions');
      return response.data;
    } catch (error) {
      console.error('Error fetching task suggestions:', error);
      throw error;
    }
  },

  acceptSuggestions: async (selections) => {
    try {
      const response = await HttpService.post('/tasks/suggestions/accept', { selections });
      return response.data;
    } catch (error) {
      console.error('Error accepting task suggestions:', error);
      throw error;
    }
  },

  dismissSuggestions: async (selections) => {
    try {
      const response = await HttpService.post('/tasks/suggestions/dismiss', { selections });
      return response.data;
    } catch (error) {
      console.error('Error dismissing task suggestions:', error);
      throw error;
    }
  },

  deleteTask: async (id) => {
    try {
      const response = await HttpService.delete(`/tasks/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting task ${id}:`, error);
      throw error;
    }
  },

  updateTaskSeries: async (id) => {
    try {
      const response = await HttpService.post(`/tasks/${id}/update-series`, {});
      return response.data;
    } catch (error) {
      console.error(`Error updating task series ${id}:`, error);
      throw error;
    }
  },

  getCategories: async () => {
    try {
      const response = await HttpService.get('/tasks/categories');
      return response.data;
    } catch (error) {
      console.error('Error fetching task categories:', error);
      throw error;
    }
  },

  // Aggregate task-enum config: {categories, frequencies, priorities,
  // statuses, seasons}, each an array of {value, label}. Use this for any
  // enum equality comparison in the form so client/server stay aligned.
  getConfig: async () => {
    try {
      const response = await HttpService.get('/tasks/config');
      return response.data;
    } catch (error) {
      console.error('Error fetching task config:', error);
      throw error;
    }
  }
};

export default TaskService;
